import { RefObject, useEffect, useRef } from 'react';

/**
 * Bind a mousedown listener that fires `onClickOutside` whenever the click
 * lands outside the element referenced by `ref`. Cleanup is automatic.
 *
 * The handler is read via a ref so callers can pass an inline arrow function
 * without `useCallback` and without rebinding the listener every render.
 *
 * Note: only binds `mousedown` — escape-key dismissal is a separate concern;
 * keep that as its own useEffect / hook in the consumer.
 */
export const useOnClickOutside = <T extends HTMLElement>(
  ref: RefObject<T | null>,
  onClickOutside: () => void,
): void => {
  const handlerRef = useRef(onClickOutside);
  handlerRef.current = onClickOutside;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handlerRef.current();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref]);
};
